import type { SelectOption } from '../../lib/types';
import { buildQueryString } from '../../lib/api-client';
import * as cache from './offer-cache';
import { clearUnitCalendarCache } from './unit-calendar';

export interface AgencyHandlerConfig {
    agencySelect: HTMLSelectElement | null;
    officeSelect: HTMLSelectElement;
    urls: {
        getOfficesForAgency: string;
        getOffersForAgency: string;
        getPartnerOffersForAgency: string;
        getVatForAgency?: string;
    };
    onLoaded: (
        accommodation: SelectOption[],
        transport: SelectOption[],
        insurance: SelectOption[]
    ) => void;
    onReset: () => void;
    onVatResolved?: (vatPct: number) => void;
}

export function setupAgencyHandler(config: AgencyHandlerConfig): void {
    const { agencySelect, officeSelect, urls, onLoaded, onReset, onVatResolved } = config;
    if (!agencySelect || agencySelect.tagName !== 'SELECT') return;

    agencySelect.addEventListener('change', async function () {
        const agencyId = (this as HTMLSelectElement).value;
        cache.clear();
        clearUnitCalendarCache();
        onReset();

        if (!agencyId) {
            officeSelect.innerHTML = '<option value="">-- Select Office --</option>';
            onLoaded([], [], []);
            return;
        }

        const vatPromise = urls.getVatForAgency
            ? fetchJson<{ vatPercentage: number }>(urls.getVatForAgency + '?' + buildQueryString({ agencyId })).catch(() => ({ vatPercentage: 0 }))
            : Promise.resolve({ vatPercentage: 0 });

        const [offices, ownOffers, partnerOffers, vatResult] = await Promise.all([
            fetchJson<any[]>(urls.getOfficesForAgency + '?' + buildQueryString({ agencyId })),
            fetchJson<any[]>(urls.getOffersForAgency + '?' + buildQueryString({ agencyId })),
            urls.getPartnerOffersForAgency
                ? fetchJson<any[]>(urls.getPartnerOffersForAgency + '?' + buildQueryString({ agencyId })).catch(() => [] as any[])
                : Promise.resolve([] as any[]),
            vatPromise,
        ]);

        officeSelect.innerHTML = '<option value="">-- Select Office --</option>';
        for (const o of offices) {
            const opt = document.createElement('option');
            opt.value = o.id;
            opt.textContent = `${o.city || ''} - ${o.address || ''}`;
            officeSelect.appendChild(opt);
        }

        const accommodation: SelectOption[] = [];
        const transport: SelectOption[] = [];
        const insurance: SelectOption[] = [];

        const partnerText = (o: any) => `${o.text || ''} ★ ${o.partnerAgencyName || ''} (+${o.earningPercentage || 0}%)`;

        for (const [list, isPartner] of [[ownOffers, false], [partnerOffers, true]] as [any[], boolean][]) {
            for (const o of list || []) {
                const text = isPartner ? partnerText(o) : (o.text || '');
                if (o.groupName === 'Accommodation') {
                    accommodation.push({ value: o.value, text, city: o.city || '', address: o.address || '' });
                } else if (o.groupName === 'Transport') {
                    transport.push({ value: o.value, text, city: o.city || '', address: o.address || '', from: o.fromDate || '', to: o.toDate || '' });
                } else if (o.groupName === 'Insurance') {
                    insurance.push({ value: o.value, text });
                }
            }
        }

        onLoaded(accommodation, transport, insurance);

        if (onVatResolved) {
            onVatResolved(vatResult.vatPercentage ?? 0);
        }
    });
}

async function fetchJson<T>(url: string): Promise<T> {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`agency-handler: HTTP ${r.status}`);
    return r.json();
}
